import {
  DB_NAME,
  DB_VERSION,
  ALERT_STORE,
  DEVICE_STORE,
  BASELINE_STORE,
  MAX_ALERTS,
} from './constants';
import type { Alert } from '../types/alerts';
import type { BaselineProfile } from '../types/baseline';

let dbInstance: IDBDatabase | null = null;

export function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      // Alerts store — autoincrement, indexed by time and type
      if (!db.objectStoreNames.contains(ALERT_STORE)) {
        const alertStore = db.createObjectStore(ALERT_STORE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        alertStore.createIndex('receivedAt', 'receivedAt', { unique: false });
        alertStore.createIndex('type', 'type', { unique: false });
      }

      // Known devices — keyed on MAC hash
      if (!db.objectStoreNames.contains(DEVICE_STORE)) {
        db.createObjectStore(DEVICE_STORE, { keyPath: 'macHash' });
      }

      // Baselines — keyed on profile name
      if (!db.objectStoreNames.contains(BASELINE_STORE)) {
        db.createObjectStore(BASELINE_STORE, { keyPath: 'name' });
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onerror = () => reject(request.error);
  });
}

/** Store an alert and auto-prune if over MAX_ALERTS */
export async function storeAlert(alert: Omit<Alert, 'id'>): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ALERT_STORE, 'readwrite');
    const store = tx.objectStore(ALERT_STORE);
    const req = store.add(alert);

    req.onsuccess = () => {
      const id = req.result as number;
      // Prune old alerts if over limit
      const countReq = store.count();
      countReq.onsuccess = () => {
        if (countReq.result > MAX_ALERTS) {
          const pruneCount = countReq.result - MAX_ALERTS;
          const index = store.index('receivedAt');
          const cursor = index.openCursor();
          let deleted = 0;
          cursor.onsuccess = () => {
            const c = cursor.result;
            if (c && deleted < pruneCount) {
              c.delete();
              deleted++;
              c.continue();
            }
          };
        }
      };
      resolve(id);
    };
    req.onerror = () => reject(req.error);
  });
}

/** Get recent alerts, newest first */
export async function getAlerts(limit = 100): Promise<Alert[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ALERT_STORE, 'readonly');
    const store = tx.objectStore(ALERT_STORE);
    const index = store.index('receivedAt');
    const alerts: Alert[] = [];

    const cursor = index.openCursor(null, 'prev');
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (c && alerts.length < limit) {
        alerts.push(c.value as Alert);
        c.continue();
      } else {
        resolve(alerts);
      }
    };
    cursor.onerror = () => reject(cursor.error);
  });
}

/** Get alert count */
export async function getAlertCount(): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ALERT_STORE, 'readonly');
    const req = tx.objectStore(ALERT_STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Clear all alerts */
export async function clearAlerts(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ALERT_STORE, 'readwrite');
    const req = tx.objectStore(ALERT_STORE).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Save a baseline profile (Float32Arrays serialized) */
export async function saveBaseline(profile: BaselineProfile): Promise<void> {
  const db = await openDB();
  // Convert typed arrays to plain arrays for IDB storage
  const serialized = {
    ...profile,
    nrfAvg: Array.from(profile.nrfAvg),
    nrfStddev: Array.from(profile.nrfStddev),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BASELINE_STORE, 'readwrite');
    const req = tx.objectStore(BASELINE_STORE).put(serialized);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Load a baseline profile by name */
export async function loadBaseline(name: string): Promise<BaselineProfile | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BASELINE_STORE, 'readonly');
    const req = tx.objectStore(BASELINE_STORE).get(name);
    req.onsuccess = () => {
      const raw = req.result;
      if (!raw) {
        resolve(null);
        return;
      }
      // Restore typed arrays
      resolve({
        ...raw,
        nrfAvg: new Float32Array(raw.nrfAvg),
        nrfStddev: new Float32Array(raw.nrfStddev),
      });
    };
    req.onerror = () => reject(req.error);
  });
}

/** List all baseline profile names */
export async function listBaselines(): Promise<string[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BASELINE_STORE, 'readonly');
    const req = tx.objectStore(BASELINE_STORE).getAllKeys();
    req.onsuccess = () => resolve(req.result as string[]);
    req.onerror = () => reject(req.error);
  });
}

/** Delete a baseline profile */
export async function deleteBaseline(name: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BASELINE_STORE, 'readwrite');
    const req = tx.objectStore(BASELINE_STORE).delete(name);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
